/**
 * Piezas de LA PUERTA (Despacho → acceso.html, validada 2026-09-16).
 * Cada una envuelve UNA pieza de system.css; ninguna inventa estilo. El
 * contrato visual vive en `.context/design/pages/despacho/acceso.html`:
 * misma estructura, mismas clases, mismos estados.
 */
import type { CSSProperties, FormHTMLAttributes, HTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { cx } from "../cx";
import { Icon, type IconName } from "../icons/Icon";
import { Skeleton } from "../patterns/Page";

/** Glifo de la tarjeta escenario (hermano de sk-empty-glyph, que no se presta). */
export function HeroGlyph({ name }: { name: IconName }) {
  return <Icon name={name} className="sk-hero-glyph" />;
}

/** Formulario dentro de la tarjeta escenario (campos alineados a la izquierda). */
export function HeroForm({ className, ...rest }: FormHTMLAttributes<HTMLFormElement>) {
  return <form className={cx("sk-heroform", className)} noValidate {...rest} />;
}

/** Columna de actos de la puerta (botones a ancho completo, apilados). */
export function HeroActs({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("sk-heroacts", className)} {...rest} />;
}

/** Pila de esqueleto en el eje de la tarjeta («abriendo tu despacho…»). */
export function HeroSkeleton() {
  return (
    <div className="sk-heroskel" aria-hidden="true">
      <Skeleton /><Skeleton /><Skeleton />
    </div>
  );
}

/** Separador con rótulo entre dos vías de la MISMA acción («o entra por»). */
export function Or({ children }: { children: ReactNode }) {
  return <div className="sk-or">{children}</div>;
}

/** Correo a la vista: mono y partible (a 320 px desbordaba tarjeta y toast). */
export function Email({ strong, children }: { strong?: boolean | undefined; children: ReactNode }) {
  return strong ? <b className="sk-email">{children}</b> : <span className="sk-email">{children}</span>;
}

/** La explicación de la tarjeta escenario: prosa larga, tinta atenuada. */
export function HeroProse({ children, style }: { children: ReactNode; style?: CSSProperties | undefined }) {
  return <p className="sk-prose sk-muted" style={style}>{children}</p>;
}

/** Fila de actos de un formulario (enviar + «ahora no»). */
export function FormActs({ children }: { children: ReactNode }) {
  return <div className="sk-formacts">{children}</div>;
}

export interface FieldShell {
  /** Base de los ids: el mando es `id`, la ayuda `id-h`, el error `id-e`. */
  id: string;
  label: ReactNode;
  required?: boolean | undefined;
  help?: ReactNode | undefined;
  /** Mensaje en línea; su presencia pinta el campo inválido. */
  error?: ReactNode | undefined;
  /**
   * Rechazo de NEGOCIO (no de formato): el valor es válido pero no se acepta
   * — «ese correo ya tiene casa». Convive con `error`, porque un campo puede
   * fallar por las dos razones y apagar solo la primera dejaba un mensaje
   * rojo bajo un campo ya válido, anunciado además como su descripción.
   */
  reject?: ReactNode | undefined;
  className?: string | undefined;
  /**
   * La etiqueta existe para el lector pero no se ve (`sk-vh`): cuando la
   * cabecera de la card ya nombra el campo a la vista (el editor de la
   * receta del simulador) y repetirlo encima sería ruido.
   */
  hideLabel?: boolean | undefined;
}

/** ids de ayuda y mensajes que el mando debe declarar en aria-describedby. */
export function describedBy({ id, help, error, reject }: FieldShell): string | undefined {
  return cx(help !== undefined ? `${id}-h` : "", error !== undefined ? `${id}-e` : "", reject !== undefined ? `${id}-r` : "") || undefined;
}

/**
 * Campo del sistema: etiqueta (+ asterisco visible de obligatoriedad),
 * mando, ayuda y mensaje de error enlazados. El mando lo pone quien compone
 * —input, select del sistema— porque cada uno declara su describedby.
 */
export function Field({ id, label, required, help, error, reject, className, hideLabel, errorAlert, children }: FieldShell & {
  children: ReactNode;
  /** El error se ANUNCIA al aparecer (`role=alert`): cuando nace de una
      acción que no mueve el foco (Enter en el campo de dimensiones del
      simulador, donde el campo se queda igual). */
  errorAlert?: boolean | undefined;
}) {
  return (
    <div className={cx("sk-field", (error !== undefined || reject !== undefined) && "sk-invalid", className)}>
      <label className={hideLabel === true ? "sk-vh" : "sk-label"} htmlFor={id}>
        {label}
        {required === true && <> <span className="sk-req" aria-hidden="true">*</span></>}
      </label>
      {children}
      {help !== undefined && <span className="sk-help" id={`${id}-h`}>{help}</span>}
      <span className="sk-error-msg" id={`${id}-e`} hidden={error === undefined} role={errorAlert === true ? "alert" : undefined}>
        <span aria-hidden="true">!</span>{error}
      </span>
      {reject !== undefined && (
        <span className="sk-error-msg" id={`${id}-r`}><span aria-hidden="true">!</span><span>{reject}</span></span>
      )}
    </div>
  );
}

/** Igual que Field pero para un mando SIN `for` (el select del sistema es un botón). */
export function FieldGroup({ id, label, required, help, error, className, group, errorAlert, children }: FieldShell & {
  children: ReactNode;
  /** `role=group` nombrado por su etiqueta: un bloque que no es un mando (la vista previa de una firma, ficha.html). */
  group?: boolean | undefined;
  errorAlert?: boolean | undefined;
}) {
  return (
    <div className={cx("sk-field", error !== undefined && "sk-invalid", className)} role={group === true ? "group" : undefined} aria-labelledby={group === true ? `${id}-l` : undefined}>
      <span className="sk-label" id={`${id}-l`}>
        {label}
        {required === true && <> <span className="sk-req" aria-hidden="true">*</span></>}
      </span>
      {children}
      {help !== undefined && <span className="sk-help" id={`${id}-h`}>{help}</span>}
      <span className="sk-error-msg" id={`${id}-e`} hidden={error === undefined} role={errorAlert === true ? "alert" : undefined}>
        <span aria-hidden="true">!</span>{error}
      </span>
    </div>
  );
}

/** Campo de texto completo: Field + el input del sistema ya cableado. */
export function TextField({ id, label, required, help, error, reject, className, style, ...input }: FieldShell & { style?: CSSProperties | undefined } & Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "className">) {
  const shell: FieldShell = {
    id, label,
    ...(required !== undefined && { required }), ...(help !== undefined && { help }),
    ...(error !== undefined && { error }), ...(reject !== undefined && { reject }),
  };
  return (
    <Field {...shell} {...(className !== undefined && { className })}>
      <input
        className="sk-input"
        id={id}
        style={style}
        aria-required={required === true ? true : undefined}
        aria-invalid={error !== undefined || reject !== undefined ? true : undefined}
        aria-describedby={describedBy(shell)}
        {...input}
      />
    </Field>
  );
}
